import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  createZadCategory,
  fetchZadCategories,
  fetchZadCategoryById,
  fetchZadCategoryOptions,
  updateZadCategory,
  updateZadCategoryStatus,
} from './service'
import {FetchZadCategoriesResponse, ZadCategoryState} from './types'

const initialState: ZadCategoryState = {
  categories: [],
  selectCategories: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  selectLoading: false,
  actionLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadCategoryState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadCategoryState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

const zadCategorySlice = createSlice({
  name: 'zadCategories',
  initialState,
  reducers: {
    clearZadCategoryError: (state: ZadCategoryState) => {
      clearError(state)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadCategories.pending, (state: ZadCategoryState) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadCategories.rejected, (state: ZadCategoryState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadCategories.fulfilled,
      (state: ZadCategoryState, action: PayloadAction<FetchZadCategoriesResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.categories = action.payload.categories
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadCategoryOptions.pending, (state: ZadCategoryState) => {
      state.selectLoading = true
    })
    builder.addCase(fetchZadCategoryOptions.rejected, (state: ZadCategoryState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.selectLoading = false
    })
    builder.addCase(fetchZadCategoryOptions.fulfilled, (state: ZadCategoryState, action) => {
      state.selectLoading = false
      state.selectCategories = action.payload.categories
    })

    builder.addCase(fetchZadCategoryById.pending, (state: ZadCategoryState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadCategoryById.rejected, (state: ZadCategoryState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(fetchZadCategoryById.fulfilled, (state: ZadCategoryState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(createZadCategory.pending, (state: ZadCategoryState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(createZadCategory.rejected, (state: ZadCategoryState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(createZadCategory.fulfilled, (state: ZadCategoryState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadCategory.pending, (state: ZadCategoryState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(updateZadCategory.rejected, (state: ZadCategoryState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(updateZadCategory.fulfilled, (state: ZadCategoryState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadCategoryStatus.pending, (state: ZadCategoryState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(updateZadCategoryStatus.rejected, (state: ZadCategoryState, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(updateZadCategoryStatus.fulfilled, (state: ZadCategoryState) => {
      state.actionLoading = false
      clearError(state)
    })
  },
})

export default zadCategorySlice.reducer
export const actions = zadCategorySlice.actions
export {initialState as zadCategoryInitialState}
